import React from 'react';
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Button from 'components/Button';
import SkeletonLoader, { ContentType } from 'components/SkeletonLoader';
import { PRIMARY_COLOR } from 'utils/constants';
import { readAxiosErr } from 'utils/misc';
import {
  SendCombinedDocEmailPayload,
  sendCombinedDocEmail,
  useCombinedDocEmailFieldsConfig,
} from 'queries/documents';

type RecipientOption = { value: number; label: string };

type AppProps = {
  documentId: number;
  onSuccess: () => any;
};

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    boxShadow: 'none',
    borderColor: state.isFocused ? PRIMARY_COLOR : '#E8E9ED',
    ':hover': { borderColor: state.isFocused ? PRIMARY_COLOR : '#E8E9ED' },
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
};

// Read-forward preview: no formatting toolbar, users just tweak the copy.
const quillModules = { toolbar: false };

// PRISM-434: email-preview form for sending a combined document's signing
// email. The recipient list, subject, and per-recipient message body (with the
// signing link already embedded) come from `email_fields_config`; the user can
// tweak subject/message before sending to one recipient at a time.
const Form: React.FC<AppProps> = ({ documentId, onSuccess }) => {
  const queryClient = useQueryClient();
  const { jobId = '' } = useParams();
  const { data: config, isLoading } =
    useCombinedDocEmailFieldsConfig(documentId);

  const [recipient, setRecipient] = React.useState<number | null>(null);
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const initialized = React.useRef(false);

  React.useEffect(() => {
    if (!config || initialized.current) return;
    initialized.current = true;
    const first = config.recipients[0]?.value ?? null;
    setRecipient(first);
    setSubject(config.subject ?? '');
    setMessage(first != null ? config.message[String(first)] ?? '' : '');
  }, [config]);

  const handleRecipientChange = (opt: RecipientOption | null) => {
    const value = opt?.value ?? null;
    setRecipient(value);
    // Re-seed the body with the newly selected recipient's prefilled message.
    if (value != null && config) {
      setMessage(config.message[String(value)] ?? '');
    }
  };

  const sendEmail = useMutation((payload: SendCombinedDocEmailPayload) =>
    sendCombinedDocEmail(documentId, payload)
  );

  const handleSend = () => {
    if (recipient == null) return;
    sendEmail.mutate(
      { recipient, subject, message },
      {
        onError: (err: any) => toast.error(readAxiosErr(err)),
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(['documents', { job: jobId }]),
            queryClient.invalidateQueries(['job-checklist', jobId]),
            queryClient.invalidateQueries(['emails']),
            queryClient.invalidateQueries(['desktop-emails']),
            queryClient.invalidateQueries(['job-communications', jobId]),
          ]);
          toast.success('Email successfully sent.');
          onSuccess();
        },
      }
    );
  };

  if (isLoading || !config) {
    return <SkeletonLoader contentType={ContentType.Form} size={5} />;
  }

  if (!config.recipients.length) {
    return (
      <div className="rounded-lg bg-gray-100 p-4 text-sm text-text-medium">
        There are no eligible signers to email yet. Add signature fields for the
        customer or rep to this document, then try again.
      </div>
    );
  }

  const selectedOption =
    config.recipients.find((r) => r.value === recipient) ?? null;

  return (
    <div className="space-y-5">
      <div>
        <label className="text-sm text-text-medium font-medium">
          Recipient
        </label>
        <Select
          className="mt-2"
          classNamePrefix="signature-packet-recipient"
          options={config.recipients}
          value={selectedOption}
          onChange={(opt) => handleRecipientChange(opt as RecipientOption)}
          isSearchable={false}
          menuPortalTarget={document.body}
          styles={selectStyles}
          components={{ IndicatorSeparator: () => null }}
        />
      </div>

      <div>
        <label className="text-sm text-text-medium font-medium">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Email Subject"
          className="mt-2 w-full border border-[#E8E9ED] rounded-lg px-4 py-2 text-sm font-medium text-black focus:outline-none focus:border-primary"
        />
      </div>

      <div>
        <label className="text-sm text-text-medium font-medium">Message</label>
        <div className="signature-packet-editor mt-2 rounded-lg border border-[#E8E9ED] overflow-hidden">
          <ReactQuill
            theme="snow"
            value={message}
            onChange={setMessage}
            modules={quillModules}
          />
        </div>
        <p className="mt-2 text-xs text-text-medium">
          A secure link for the recipient to review and sign this document
          remotely is included in the message above.
        </p>
      </div>

      <Button
        label={sendEmail.isLoading ? 'Sending... (Please wait)' : 'Send Email'}
        className="mt-2"
        loading={sendEmail.isLoading}
        disabled={recipient == null || sendEmail.isLoading}
        onClick={handleSend}
      />
    </div>
  );
};

export default Form;
